// SPDX-License-Identifier: Elastic-2.0
/**
 * AccountChip — sidebar header tile (T-020).
 *
 * Two display modes:
 *
 *   - **Full** (default): 36px gradient avatar + name + tier badge +
 *     chevron. Rendered as the top tile of the persistent sidebar.
 *
 *   - **Compact** (`compact`): just the avatar circle. Used by the
 *     TopBar's right-rail action cluster (the chip serves as the
 *     account-icon button next to the bell).
 *
 * Initials derivation:
 *
 *   "Casa Palermo"  → "CP"
 *   "Mirlai"        → "M"   (single word — first letter only)
 *   "  "            → "M"   (blank / whitespace → "M" sentinel)
 *   undefined       → "M"
 *
 * Falling back to the literal "M" (matches the brand mark) keeps the
 * avatar visually anchored even when the user profile hasn't loaded yet.
 *
 * Tier badge: rendered for cloud users only (Free / Plus / Pro). When
 * `user`/`tier` are null (local-only or unauthenticated) we skip the
 * badge entirely rather than show a misleading "Free" label.
 *
 * The full-mode chip is a button (clickable surface ⇒ `<button>`),
 * navigating to /account on click. The compact-mode chip is also a
 * button so it can carry an `aria-label` and `onClick` without extra
 * wrapper divs.
 */
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icon } from "../../components/Icon/Icon";
import { useAuth } from "../../api/auth-store";
import { usePairedDevices } from "../settings/useSettings";

import styles from "./DashboardLayout.module.css";

/** Localised label per tier. Kept inline; only three entries. */
const TIER_LABEL: Record<NonNullable<ReturnType<typeof useAuth.getState>["tier"]>, string> = {
  free: "Free",
  plus: "Plus",
  pro: "Pro",
};

/**
 * Compute up-to-2-character initials from a display name. Pure function
 * (no React); the tests import it directly to lock the table of inputs
 * above without spinning up render machinery.
 *
 * Co-located with the only consumer (AccountChip) — extracting to a
 * separate utils file isn't in T-020's allowed file list, and the Fast
 * Refresh warning is acceptable for a pure helper that has no live
 * binding to the component itself.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function computeInitials(name: string | null | undefined): string {
  if (!name) return "M";
  const trimmed = name.trim();
  if (!trimmed) return "M";
  const parts = trimmed.split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  const result = `${first}${last}`.toUpperCase();
  return result || "M";
}

export interface AccountChipProps {
  /** Compact mode: avatar-only, used in the TopBar right rail. */
  compact?: boolean;
  /** Optional aria-label override (defaults to a sensible per-mode label). */
  "aria-label"?: string;
  /** Optional click override; defaults to navigating to /account. */
  onClick?: () => void;
  /** Optional class for layout/positioning overrides. */
  className?: string;
}

export function AccountChip({
  compact = false,
  onClick,
  className,
  "aria-label": ariaLabel,
}: AccountChipProps): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAuth((s) => s.user);
  const tier = useAuth((s) => s.tier);

  // F-342: composite subtitle "Free · 1 home" pulls the home count
  // from the same paired-devices query HomesPage uses. Local-only
  // mode → 1 home (the local box itself); cloud → query result.
  // Defaults to 1 when the query is still loading so the chip never
  // flashes "0 homes" on initial mount.
  const { data: homes } = usePairedDevices();
  const homesCount = homes?.length ?? 1;

  const initials = computeInitials(user?.display_name);
  const tierLabel = tier ? TIER_LABEL[tier] : null;
  const homesLabel = t("account_chip.homes_count", { count: homesCount });
  const subtitle = tierLabel ? `${tierLabel} · ${homesLabel}` : homesLabel;
  const handleClick = onClick ?? (() => navigate("/account"));

  if (compact) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={[styles.accountChipCompact, className].filter(Boolean).join(" ")}
        aria-label={ariaLabel ?? "Account"}
        data-testid="account-chip-compact"
      >
        <span className={styles.accountAvatar} aria-hidden="true">
          {initials}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={[styles.accountChip, className].filter(Boolean).join(" ")}
      aria-label={ariaLabel ?? `Account: ${user?.display_name ?? "Mirlai"}`}
      data-testid="account-chip"
    >
      <span className={styles.accountAvatar} aria-hidden="true">
        {initials}
      </span>
      <span className={styles.accountText}>
        <span className={styles.accountName}>{user?.display_name ?? "Mirlai"}</span>
        <span className={styles.accountSubtitle} data-tier={tier ?? "free"}>
          {subtitle}
        </span>
      </span>
      <Icon name="chevron" size={16} className={styles.accountChevron} />
    </button>
  );
}
